import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { RequestEventType } from '@vendasta/customer-voice-service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, debounceTime, first, map } from 'rxjs/operators';
import {
  NO_LISTING_BUILDER_ERROR,
  NO_PREFERRED_SITES_SELECTED_ERROR,
  ReviewRequestType,
  TOO_MANY_CUSTOMER_SELECTED_FOR_SMS,
  WOULD_GO_OVER_MONTHLY_LIMIT_FOR_SMS,
} from '../../common/constants';
import { Edition } from '../../common/editions';
import { Validator } from '../../common/validator';
import { AppConfigService } from '../../core/app-config.service';
import { CustomerVoiceRouterService } from '../../core/cv-router.service';
import { EmailUsagesService } from '../../core/email-usages.service';
import { SmsUsagesService } from '../../core/sms-usages.service';
import { Template, TemplateListingService } from '../../core/template-listing.service';
import { Customer, NewCustomerJson } from '../../customers/customer-common';
import { CustomerSelectionService } from '../../customers/customer-selection.service';
import { CustomerService, customersFromResponse } from '../../customers/customers.service';
import { SendEmailService } from '../../preview-send-email/send-email.service';
import { SendSmsService } from '../../preview-send-sms/send-sms.service';
import { ReviewRequestEventService } from '../../review-request-event/review-request-event.service';
import { ExpressCtaDialogService } from '../express-cta-dialog/express-cta-dialog.service';

@Component({
  selector: 'app-send-single-request-dialog',
  templateUrl: './send-single-request-dialog.component.html',
  styleUrls: ['./send-single-request-dialog.component.scss'],
  standalone: false,
})
export class SendSingleRequestDialogComponent implements OnInit {
  form: UntypedFormGroup;
  selectedTemplate: UntypedFormControl;
  savingInProgress = false;
  customerWithEmailExists = false;
  customerWithPhoneExists = false;
  accountGroupId = '';

  ReviewRequestType = ReviewRequestType;

  templates: Template[];
  allTemplates$$: BehaviorSubject<Template[]> = new BehaviorSubject<Template[]>([]);
  filteredTemplates$$: BehaviorSubject<Template[]> = new BehaviorSubject<Template[]>([]);

  reachedExpressEmailLimit$: Observable<boolean>;

  clickTrackingLabel = 'email-limit-reached-nav-express-cta';
  hasSmsEnabled$: Observable<boolean>;

  customersPageLink: string;

  constructor(
    public dialogRef: MatDialogRef<SendSingleRequestDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private customerService: CustomerService,
    private templateListingService: TemplateListingService,
    private sendEmailService: SendEmailService,
    private customerSelectionService: CustomerSelectionService,
    private customerVoiceRouter: CustomerVoiceRouterService,
    private appConfigService: AppConfigService,
    private emailUsageService: EmailUsagesService,
    private ctaDialogService: ExpressCtaDialogService,
    private sendSmsService: SendSmsService,
    private snowplowService: ProductAnalyticsService,
    private reviewRequestEventService: ReviewRequestEventService,
    private smsUsagesService: SmsUsagesService,
  ) {}

  ngOnInit(): void {
    this.customerVoiceRouter.accountGroupId.subscribe((accountGroupId) => (this.accountGroupId = accountGroupId));

    this.hasSmsEnabled$ = this.smsUsagesService.hasSmsEnabled$;

    this.selectedTemplate = new UntypedFormControl();

    this.form = this.formBuilder.group({
      firstName: [null, Validator.isName],
      lastName: [null, Validator.isName],
      emailAddress: [null, Validator.isEmail, this.emailAlreadyExists.bind(this)],
      phoneNumber: [null, Validator.isPhoneNumber, this.phoneAlreadyExists.bind(this)],
      tag: null,
      employeeFirstName: [null, Validator.isName],
      employeeLastName: [null, Validator.isName],
      selectedTemplate: this.selectedTemplate,
    });

    this.reachedExpressEmailLimit$ = combineLatest([
      this.appConfigService.appConfig,
      this.emailUsageService.emailUsages$,
    ]).pipe(
      map(([config, usages]) => {
        return config.edition === Edition.Express && usages.monthlyUsage >= usages.monthlyLimit;
      }),
    );

    this.templateListingService.listTemplates();
    this.templateListingService.templates$.subscribe((templates) => this.allTemplates$$.next(templates));

    this.templateListingService.fetchDefaultTemplateId();

    this.form.valueChanges.pipe(debounceTime(300)).subscribe((form) => {
      let templates = this.allTemplates$$.getValue();

      if (form.emailAddress === '' || form.emailAddress === null) {
        templates = templates.filter((template) => template.templateType !== ReviewRequestType.email);
      }
      if (form.phoneNumber === '' || form.phoneNumber === null) {
        templates = templates.filter((template) => template.templateType !== ReviewRequestType.sms);
      }

      if (form.selectedTemplate == null && templates.length > 0) {
        this.selectedTemplate.setValue(templates[0]);
      } else if (form.selectedTemplate !== null && templates.length === 0) {
        this.selectedTemplate.setValue(null);
        this.selectedTemplate.markAsUntouched();
      }
      this.filteredTemplates$$.next(templates);
    });

    this.customersPageLink = `/cv/account/${this.accountGroupId}/customers`;
  }

  emailAlreadyExists(control: UntypedFormControl): Observable<{ alreadyExists: boolean }> {
    this.customerWithEmailExists = false;
    if (!control.value) {
      return of(null);
    }

    return this.customerService.customerExists(control.value, '').pipe(
      map((response) => {
        if (response.body.data) {
          this.customerWithEmailExists = true;
          return { alreadyExists: true };
        }
        return null;
      }),
      first(),
    );
  }

  phoneAlreadyExists(
    control: UntypedFormControl,
  ): Observable<{ invalidPhoneNumber: boolean } | { alreadyExists: boolean } | null> {
    this.customerWithPhoneExists = false;
    if (!control.value) {
      return of(null);
    }

    return this.customerService.customerExists('', control.value).pipe(
      map((response) => {
        if (response.body.data) {
          this.customerWithPhoneExists = true;
          return { alreadyExists: true };
        }
        return null;
      }),
      first(),
      catchError(() => of({ invalidPhoneNumber: true })),
    );
  }

  close(): void {
    this.dialogRef.close(null);
  }

  submitForm(): void {
    this.savingInProgress = true;

    if (!this.form.valid) {
      this.snackbarService.errorSnack(this.translateService.instant('ADD_CUSTOMER_FORM.INVALID_FIELDS'));
      this.savingInProgress = false;
      return;
    }

    const newCustomer: NewCustomerJson = {
      first_name: this.form.value.firstName,
      last_name: this.form.value.lastName,
      email: this.form.value.emailAddress,
      phone: this.form.value.phoneNumber,
      tag: this.form.value.tag,
      employee_first_name: this.form.value.employeeFirstName,
      employee_last_name: this.form.value.employeeLastName,
    };

    if (!newCustomer.email && !newCustomer.phone) {
      this.snackbarService.errorSnack(this.translateService.instant('ADD_CUSTOMER_FORM.EMAIL_OR_PHONE_MISSING'));
      this.savingInProgress = false;
      return;
    }

    const template: Template = this.selectedTemplate.value;
    const emailTemplateSelected = template.templateType === ReviewRequestType.email;
    const smsTemplateSelected = template.templateType === ReviewRequestType.sms;

    if (
      (this.customerWithEmailExists && emailTemplateSelected) ||
      (this.customerWithPhoneExists && smsTemplateSelected)
    ) {
      this.customerService
        .getCustomersBySearchTerm(emailTemplateSelected ? newCustomer.email : newCustomer.phone)
        .subscribe(
          (result) => {
            const customer: Customer = customersFromResponse(result.body.data['customers'])[0];

            if (emailTemplateSelected) {
              this.sendEmail(customer, template);
            } else {
              this.sendSMS(customer, template);
            }

            this.dialogRef.close(customer);
          },
          () => {
            this.savingInProgress = false;

            this.snackbarService.errorSnack(this.translateService.instant('ADD_CUSTOMER_FORM.AN_ERROR_OCCURRED'));
          },
        );

      return;
    }

    if (this.customerWithEmailExists || this.customerWithPhoneExists) {
      this.snackbarService.errorSnack(this.translateService.instant('ADD_CUSTOMER_FORM.CUSTOMER_ALREADY_EXISTS'));
      this.savingInProgress = false;
      return;
    }

    this.customerService.addCustomers([newCustomer]).subscribe(
      (result) => {
        const customer: Customer = customersFromResponse(result.body.data['customers'])[0];
        if (emailTemplateSelected) {
          this.sendEmail(customer, template);
        } else {
          this.sendSMS(customer, template);
        }

        this.dialogRef.close(customer);
      },
      (error) => {
        this.savingInProgress = false;
        if (error.status === 409) {
          this.snackbarService.errorSnack(this.translateService.instant('ADD_CUSTOMER_FORM.CUSTOMER_ALREADY_EXISTS'));
        } else {
          this.snackbarService.errorSnack(this.translateService.instant('ADD_CUSTOMER_FORM.AN_ERROR_OCCURRED'));
        }
      },
    );
  }

  sendEmail(customer: Customer, template: Template): void {
    const templateIdToSend = this.constructFullTemplateId(template);
    this.sendEmailService.sendReviewRequestEmail([customer], templateIdToSend).subscribe({
      next: () => {
        this.customerService.customersRecentlySentEmail(this.customerSelectionService.getSelectedCustomers());
        this.reviewRequestEventService
          .createReviewRequestEventFromCustomers([customer], RequestEventType.REQUEST_EVENT_TYPE_EMAIL)
          .subscribe();
        this.snackbarService.successSnack(this.translateService.instant('CUSTOMERS.PREVIEW_EMAIL.EMAIL_SEND_SUCCESS'));
      },
      error: (response: HttpErrorResponse) => {
        if (response.status === 412) {
          if (response.error.message === NO_PREFERRED_SITES_SELECTED_ERROR) {
            // No preferred sites are selected for the external workflow
            this.sendEmailService.openNoPreferredSitesDialog();
          } else if (response.error.message === NO_LISTING_BUILDER_ERROR) {
            this.sendEmailService.openNoListingBuilderDialog();
          }
        } else {
          this.snackbarService.errorSnack(
            this.translateService.instant('ADD_CUSTOMER_FORM.SOMETHING_WENT_WRONG_SENDING_EMAIL'),
          );
        }
      },
    });
  }

  sendSMS(customer: Customer, template: Template): void {
    this.sendSmsService.sendReviewRequestSms([customer], template.templateId).subscribe({
      next: () => {
        this.customerService.customersRecentlySentSms(this.customerSelectionService.getSelectedCustomers());
        this.reviewRequestEventService
          .createReviewRequestEventFromCustomers([customer], RequestEventType.REQUEST_EVENT_TYPE_SMS)
          .subscribe();
        this.snackbarService.successSnack(this.translateService.instant('CUSTOMERS.PREVIEW_EMAIL.SMS_SEND_SUCCESS'));
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 412) {
          if (error.error.message === NO_PREFERRED_SITES_SELECTED_ERROR) {
            this.sendSmsService.openNoPreferredSitesDialog();
          } else if (error.error.message === TOO_MANY_CUSTOMER_SELECTED_FOR_SMS) {
            this.snackbarService.errorSnack(
              this.translateService.instant('ADD_CUSTOMER_FORM.MAP_CSV.SMS_DAILY_LIMIT_ERROR'),
            );
          } else if (error.error.message === WOULD_GO_OVER_MONTHLY_LIMIT_FOR_SMS) {
            this.snackbarService.errorSnack(
              this.translateService.instant('ADD_CUSTOMER_FORM.MAP_CSV.SMS_MONTHLY_LIMIT_ERROR'),
            );
          } else {
            this.snackbarService.errorSnack(error.error.message);
          }
        } else {
          this.snackbarService.errorSnack(
            this.translateService.instant('ADD_CUSTOMER_FORM.SOMETHING_WENT_WRONG_SENDING_SMS'),
          );
        }
      },
    });
  }

  constructFullTemplateId(template: Template): string {
    return `/account-group-id/${this.accountGroupId}/type/${template.templateType}/template-id/${template.templateId}`;
  }

  openUpgradeDialog(): void {
    this.ctaDialogService.open(
      'https://vstatic-prod.apigateway.co/customer-voice-client.9223372035073077600.prod/assets/images/express_cta/unlimited_emails.png',
      'EXPRESS_CTA_DIALOG.IMAGE_TEXT.EMAIL_USAGE',
      `${this.clickTrackingLabel}-upgraded`,
    );
  }
}
